import React from "react";
import { UserSpaceStats, UserStats } from "../types/user.entity";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import {
  FaCalendar,
  FaChartBar,
  FaCoins,
  FaBookOpen,
  FaChalkboardTeacher,
  FaUsers,
  FaChartLine,
  FaStar,
} from "react-icons/fa";
import { UserStatisticsSkeleton } from "./UserStatisticsSkeleton";
import {
  formatDuration,
  formatCurrency,
  validateUserStats,
  getTotalBookings,
  getTotalLearningTime,
  getTotalCoachingTime,
  prepareChartData,
} from "../utils/user-stats.util";

interface UserStatisticsProps {
  stats: UserStats | null;
  spaceStats: UserSpaceStats[];
  isLoading: boolean;
}

const getSpaceTotalBookings = (stats: UserSpaceStats) =>
  (stats.bookingStats?.awaitingCount ?? 0) +
  (stats.bookingStats?.upcomingCount ?? 0) +
  (stats.bookingStats?.completedCount ?? 0) +
  (stats.bookingStats?.unattendedCount ?? 0);

const getSpaceLearningMinutes = (stats: UserSpaceStats) =>
  (stats.learningDurationMinutes?.directCall ?? 0) +
  (stats.learningDurationMinutes?.scheduled ?? 0) +
  (stats.learningDurationMinutes?.ai ?? 0);

const getSpaceCoachingMinutes = (stats: UserSpaceStats) =>
  (stats.coachingDurationMinutes?.directCall ?? 0) +
  (stats.coachingDurationMinutes?.scheduled ?? 0);

export const UserStatistics: React.FC<UserStatisticsProps> = ({
  stats,
  spaceStats,
  isLoading,
}) => {
  const hasGlobalStats = !!stats && validateUserStats(stats);
  const chartData = hasGlobalStats ? prepareChartData(stats) : null;

  if (isLoading) {
    return <UserStatisticsSkeleton />;
  }

  if (!hasGlobalStats && spaceStats.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full mx-auto mb-6 flex items-center justify-center">
            <FaChartBar className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Statistics Available
          </h3>
          <p className="text-gray-600 text-lg">
            Statistics for this user are not available yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4">
          <FaChartBar className="text-white text-2xl" />
        </div>
        <h3 className="text-3xl font-bold text-gray-900 mb-2">
          User Statistics Dashboard
        </h3>
        <p className="text-gray-600 text-lg">
          Comprehensive overview of user performance and activities
        </p>
      </div>

      <div className="space-y-10">
        {hasGlobalStats && chartData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="group bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                    <FaCoins className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-900">
                      {formatCurrency(stats.karma?.totalSpent ?? 0)}
                    </div>
                    <div className="text-sm font-medium text-red-700">
                      Total Spent
                    </div>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <FaChartLine className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-900">
                      {formatCurrency(stats.karma?.totalPurchased ?? 0)}
                    </div>
                    <div className="text-sm font-medium text-indigo-700">
                      Total Purchased
                    </div>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <FaStar className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-900">
                      {formatCurrency(stats.karma?.totalEarned ?? 0)}
                    </div>
                    <div className="text-sm font-medium text-green-700">
                      Total Earned
                    </div>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <FaUsers className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-900">
                      {formatCurrency(stats.karma?.totalEarnedFromReferrals ?? 0)}
                    </div>
                    <div className="text-sm font-medium text-purple-700">
                      Referral Earnings
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <FaCalendar className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-blue-900 mb-2">
                    {getTotalBookings(stats)}
                  </div>
                  <div className="text-lg font-medium text-blue-700">
                    Total Bookings
                  </div>
                  <div className="mt-4 text-sm text-blue-600">
                    <div className="flex justify-between mb-2">
                      <span className="px-2 py-1 bg-blue-100 rounded-lg">
                        Awaiting: {stats.bookingStats?.awaitingCount || 0}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 rounded-lg">
                        Upcoming: {stats.bookingStats?.upcomingCount || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="px-2 py-1 bg-blue-100 rounded-lg">
                        Completed: {stats.bookingStats?.completedCount || 0}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 rounded-lg">
                        Unattended: {stats.bookingStats?.unattendedCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <FaBookOpen className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-yellow-900 mb-2">
                    {formatDuration(getTotalLearningTime(stats))}
                  </div>
                  <div className="text-lg font-medium text-yellow-700">
                    Learning Time
                  </div>
                  <div className="mt-4 text-sm text-yellow-600">
                    <div className="flex justify-between">
                      <span>
                        Direct:{" "}
                        {formatDuration(stats.learningDurationMinutes?.directCall || 0)}
                      </span>
                      <span>
                        Scheduled:{" "}
                        {formatDuration(stats.learningDurationMinutes?.scheduled || 0)}
                      </span>
                    </div>
                    <div className="text-center mt-1">
                      <span>
                        AI: {formatDuration(stats.learningDurationMinutes?.ai || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <FaChalkboardTeacher className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-teal-900 mb-2">
                    {formatDuration(getTotalCoachingTime(stats))}
                  </div>
                  <div className="text-lg font-medium text-teal-700">
                    Coaching Time
                  </div>
                  <div className="mt-4 text-sm text-teal-600">
                    <div className="flex justify-between">
                      <span>
                        Direct:{" "}
                        {formatDuration(stats.coachingDurationMinutes?.directCall || 0)}
                      </span>
                      <span>
                        Scheduled:{" "}
                        {formatDuration(stats.coachingDurationMinutes?.scheduled || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8 shadow-xl border border-blue-200">
                <div className="flex items-center justify-between mb-8">
                  <h4 className="text-2xl font-bold text-gray-800 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mr-4 flex items-center justify-center">
                      <FaCalendar className="text-white text-lg" />
                    </div>
                    Booking Statistics
                  </h4>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-900">
                      {getTotalBookings(stats)}
                    </div>
                    <div className="text-sm text-blue-600">Total Bookings</div>
                  </div>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData.bookingData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#6b7280", fontSize: 12, fontWeight: 600 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#6b7280", fontSize: 12, fontWeight: 600 }}
                        tickFormatter={(value) => Math.round(Number(value)).toString()}
                      />
                      <Tooltip
                        formatter={(value) => [Math.round(Number(value)), "Count"]}
                        labelStyle={{ color: "#374151", fontWeight: 600 }}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          borderRadius: "12px",
                          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={50}>
                        {chartData.bookingData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {chartData.withdrawalData.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-3xl p-8 shadow-xl border border-purple-200">
                  <div className="flex items-center justify-between mb-8">
                    <h4 className="text-2xl font-bold text-gray-800 flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl mr-4 flex items-center justify-center">
                        <FaChartLine className="text-white text-lg" />
                      </div>
                      Withdrawal History
                    </h4>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-purple-900">
                        {chartData.withdrawalData.reduce((sum, item) => sum + item.count, 0)}
                      </div>
                      <div className="text-sm text-purple-600">Total Withdrawals</div>
                    </div>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData.withdrawalData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#e5e7eb"
                          opacity={0.3}
                        />
                        <XAxis
                          dataKey="year"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#6b7280", fontSize: 12, fontWeight: 600 }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#6b7280", fontSize: 12, fontWeight: 600 }}
                        />
                        <Tooltip
                          formatter={(value) => [value, "Count"]}
                          labelStyle={{ color: "#374151", fontWeight: 600 }}
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#8b5cf6"
                          strokeWidth={3}
                          fill="url(#purpleGradient)"
                          fillOpacity={0.6}
                        />
                        <defs>
                          <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-br from-yellow-50 to-orange-100 rounded-3xl p-8 shadow-xl border border-yellow-200">
                <div className="flex items-center justify-between mb-8">
                  <h4 className="text-2xl font-bold text-gray-800 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl mr-4 flex items-center justify-center">
                      <FaBookOpen className="text-white text-lg" />
                    </div>
                    Learning Duration Distribution
                  </h4>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-yellow-900">
                      {formatDuration(getTotalLearningTime(stats))}
                    </div>
                    <div className="text-sm text-yellow-600">
                      Total Learning Time
                    </div>
                  </div>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.learningDurationData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) =>
                          `${name}: ${formatDuration(value || 0)}`
                        }
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.learningDurationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          formatDuration(value as number),
                          "Duration",
                        ]}
                        labelStyle={{ color: "#374151", fontWeight: 600 }}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          borderRadius: "12px",
                          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-100 rounded-3xl p-8 shadow-xl border border-teal-200">
                <div className="flex items-center justify-between mb-8">
                  <h4 className="text-2xl font-bold text-gray-800 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl mr-4 flex items-center justify-center">
                      <FaChalkboardTeacher className="text-white text-lg" />
                    </div>
                    Coaching Duration Distribution
                  </h4>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-teal-900">
                      {formatDuration(getTotalCoachingTime(stats))}
                    </div>
                    <div className="text-sm text-teal-600">
                      Total Coaching Time
                    </div>
                  </div>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.coachingDurationData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) =>
                          `${name}: ${formatDuration(value || 0)}`
                        }
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.coachingDurationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          formatDuration(value as number),
                          "Duration",
                        ]}
                        labelStyle={{ color: "#374151", fontWeight: 600 }}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          borderRadius: "12px",
                          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </>
        )}

        {spaceStats.length > 0 && (
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-8 shadow-xl border border-slate-200">
            <div className="flex items-center justify-between mb-8">
              <h4 className="text-2xl font-bold text-gray-800 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl mr-4 flex items-center justify-center">
                  <FaUsers className="text-white text-lg" />
                </div>
                User Space Stats
              </h4>
              <div className="text-right">
                <div className="text-3xl font-bold text-slate-900">
                  {spaceStats.length}
                </div>
                <div className="text-sm text-slate-600">Spaces</div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {spaceStats.map((spaceStat) => (
                <div
                  key={spaceStat.id}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-lg font-bold text-slate-900">
                        {spaceStat.type} | {String(spaceStat.langCode).toUpperCase()}
                      </div>
                      <div className="text-sm text-slate-500 font-mono">
                        {spaceStat.spaceId}
                      </div>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      {spaceStat.type}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="rounded-xl bg-blue-50 p-4">
                      <div className="text-sm text-blue-700 font-medium">
                        Bookings
                      </div>
                      <div className="text-2xl font-bold text-blue-900">
                        {getSpaceTotalBookings(spaceStat)}
                      </div>
                    </div>
                    <div className="rounded-xl bg-yellow-50 p-4">
                      <div className="text-sm text-yellow-700 font-medium">
                        Learning
                      </div>
                      <div className="text-2xl font-bold text-yellow-900">
                        {formatDuration(getSpaceLearningMinutes(spaceStat))}
                      </div>
                    </div>
                    <div className="rounded-xl bg-teal-50 p-4">
                      <div className="text-sm text-teal-700 font-medium">
                        Coaching
                      </div>
                      <div className="text-2xl font-bold text-teal-900">
                        {formatDuration(getSpaceCoachingMinutes(spaceStat))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm text-slate-700">
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      Awaiting: {spaceStat.bookingStats?.awaitingCount ?? 0}
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      Upcoming: {spaceStat.bookingStats?.upcomingCount ?? 0}
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      Completed: {spaceStat.bookingStats?.completedCount ?? 0}
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      Unattended: {spaceStat.bookingStats?.unattendedCount ?? 0}
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      Joined Rooms: {spaceStat.liveRoomsStats?.joined?.roomsCount ?? 0}
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      Hosted Rooms:{" "}
                      {(spaceStat.liveRoomsStats?.hosted?.practiceRoomsCount ?? 0) +
                        (spaceStat.liveRoomsStats?.hosted?.communityRoomsCount ?? 0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
